import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, ContentCopy as CopyIcon,
  RemoveCircleOutline as RemoveIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';

const specialties = [
  'Clínica Geral', 'Ortodontia', 'Endodontia', 'Periodontia', 'Implantodontia',
  'Prótese Dentária', 'Cirurgia Bucomaxilofacial', 'Odontopediatria', 'Estética',
  'Cardiologia', 'Dermatologia', 'Ginecologia', 'Ortopedia', 'Pediatria',
  'Psiquiatria', 'Oftalmologia', 'Otorrino', 'Neurologia', 'Urologia',
];

const fieldTypes = [
  { value: 'text', label: 'Texto Curto' },
  { value: 'textarea', label: 'Texto Longo' },
  { value: 'select', label: 'Seleção Única' },
  { value: 'multiselect', label: 'Seleção Múltipla' },
  { value: 'checkbox', label: 'Sim/Não' },
  { value: 'number', label: 'Número' },
  { value: 'date', label: 'Data' },
  { value: 'radio', label: 'Opções (Radio)' },
];

const AnamnesisTemplates: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });

  const [form, setForm] = useState<any>({
    name: '', specialty: '', description: '', is_active: true,
    sections: [{ title: 'Informações Gerais', fields: [] }],
  });

  useEffect(() => { loadTemplates(); }, [search, filterSpecialty]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 50 };
      if (search) params.search = search;
      if (filterSpecialty) params.specialty = filterSpecialty;
      const { data } = await axios.get(`${getApiUrl()}/api/clinic/anamnesis-templates`, { params });
      setTemplates(data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, fields: form.sections };
      if (editing) {
        await axios.put(`${getApiUrl()}/api/clinic/anamnesis-templates/${editing.id}`, payload);
        setSnackbar({ open: true, message: 'Modelo atualizado!', severity: 'success' });
      } else {
        await axios.post(`${getApiUrl()}/api/clinic/anamnesis-templates`, payload);
        setSnackbar({ open: true, message: 'Modelo criado!', severity: 'success' });
      }
      setOpenDialog(false); resetForm(); loadTemplates();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir modelo?')) return;
    try {
      await axios.delete(`${getApiUrl()}/api/clinic/anamnesis-templates/${id}`);
      setSnackbar({ open: true, message: 'Excluído!', severity: 'success' }); loadTemplates();
    } catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const handleEdit = (template: any) => {
    setEditing(template);
    setForm({
      name: template.name || '', specialty: template.specialty || '',
      description: template.description || '', is_active: template.is_active !== false,
      sections: template.fields || [{ title: 'Informações Gerais', fields: [] }],
    });
    setOpenDialog(true);
  };

  const handleDuplicate = async (template: any) => {
    try {
      await axios.post(`${getApiUrl()}/api/clinic/anamnesis-templates`, {
        ...template, name: `${template.name} (Cópia)`, id: undefined, created_at: undefined, updated_at: undefined,
      });
      setSnackbar({ open: true, message: 'Modelo duplicado!', severity: 'success' }); loadTemplates();
    } catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const resetForm = () => {
    setEditing(null);
    setForm({ name: '', specialty: '', description: '', is_active: true, sections: [{ title: 'Informações Gerais', fields: [] }] });
  };

  const addSection = () => {
    setForm({ ...form, sections: [...form.sections, { title: `Seção ${form.sections.length + 1}`, fields: [] }] });
  };

  const removeSection = (idx: number) => {
    setForm({ ...form, sections: form.sections.filter((_: any, i: number) => i !== idx) });
  };

  const addField = (sectionIdx: number) => {
    const sections = [...form.sections];
    sections[sectionIdx].fields.push({ label: '', type: 'text', required: false, options: '' });
    setForm({ ...form, sections });
  };

  const removeField = (sectionIdx: number, fieldIdx: number) => {
    const sections = [...form.sections];
    sections[sectionIdx].fields.splice(fieldIdx, 1);
    setForm({ ...form, sections });
  };

  const updateField = (sectionIdx: number, fieldIdx: number, key: string, value: any) => {
    const sections = [...form.sections];
    sections[sectionIdx].fields[fieldIdx][key] = value;
    setForm({ ...form, sections });
  };

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Modelos de Anamnese</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Crie modelos de anamnese personalizados por especialidade</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadTemplates} sx={{ color: '#14B8A6' }}><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }} onClick={() => { resetForm(); setOpenDialog(true); }}>
            Novo Modelo
          </Button>
        </Box>
      </Box>

      {/* Search/Filters */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', p: 2, '&:last-child': { pb: 2 } }}>
          <TextField size="small" placeholder="Buscar modelos..." value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ minWidth: 250 }} />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Especialidade</InputLabel>
            <Select value={filterSpecialty} label="Especialidade" onChange={(e) => setFilterSpecialty(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {specialties.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={40} /></Box>
      ) : templates.length === 0 ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: '#94a3b8' }}>Nenhum modelo de anamnese encontrado</Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {templates.map((t) => (
            <Grid item xs={12} sm={6} md={4} key={t.id}>
              <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', transition: '0.2s', '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' } }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                    <Chip label={t.is_active ? 'Ativo' : 'Inativo'} size="small"
                      sx={{ bgcolor: t.is_active ? '#dcfce7' : '#fee2e2', color: t.is_active ? '#22c55e' : '#ef4444', fontWeight: 600, fontSize: '0.65rem' }} />
                  </Box>
                  <Chip label={t.specialty || 'Geral'} size="small" sx={{ mb: 1, bgcolor: '#14B8A615', color: '#14B8A6', fontWeight: 600, fontSize: '0.7rem' }} />
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2, minHeight: 40 }}>{t.description || 'Sem descrição'}</Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>{Array.isArray(t.fields) ? t.fields.reduce((sum: number, s: any) => sum + (s.fields?.length || 0), 0) : 0} campos</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 1, justifyContent: 'flex-end' }}>
                    <Chip label="Duplicar" size="small" icon={<CopyIcon sx={{ fontSize: '0.8rem !important' }} />} onClick={() => handleDuplicate(t)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#e2e8f0' } }} />
                    <IconButton size="small" onClick={() => handleEdit(t)} sx={{ color: '#14B8A6' }}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(t.id)} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: isMobile
            ? { m: 0, borderRadius: 0, height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column' }
            : {},
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, px: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{editing ? 'Editar' : 'Novo'} Modelo de Anamnese</Typography>
          <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Nome do Modelo" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small"><InputLabel>Especialidade</InputLabel>
                <Select value={form.specialty} label="Especialidade" onChange={(e) => setForm({ ...form, specialty: e.target.value })}>
                  {specialties.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select></FormControl>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Descrição" multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Seções e Campos</Typography>

          {form.sections.map((section: any, sIdx: number) => (
            <Card key={sIdx} sx={{ mb: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                  <TextField size="small" label="Nome da Seção" value={section.title}
                    onChange={(e) => { const s = [...form.sections]; s[sIdx].title = e.target.value; setForm({ ...form, sections: s }); }} sx={{ flex: 1 }} />
                  {form.sections.length > 1 && (
                    <IconButton size="small" onClick={() => removeSection(sIdx)} sx={{ color: '#ef4444' }}><RemoveIcon /></IconButton>
                  )}
                </Box>

                {section.fields.map((field: any, fIdx: number) => (
                  <Box key={fIdx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', pl: 2, borderLeft: '3px solid #14B8A622' }}>
                    <TextField size="small" label="Pergunta/Label" value={field.label} sx={{ flex: 2 }}
                      onChange={(e) => updateField(sIdx, fIdx, 'label', e.target.value)} />
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Tipo</InputLabel>
                      <Select value={field.type} label="Tipo" onChange={(e) => updateField(sIdx, fIdx, 'type', e.target.value)}>
                        {fieldTypes.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    {(field.type === 'select' || field.type === 'multiselect' || field.type === 'radio') && (
                      <TextField size="small" label="Opções (separar por vírgula)" value={field.options} sx={{ flex: 1 }}
                        onChange={(e) => updateField(sIdx, fIdx, 'options', e.target.value)} />
                    )}
                    <Chip label={field.required ? 'Obrig.' : 'Opcional'} size="small" onClick={() => updateField(sIdx, fIdx, 'required', !field.required)}
                      sx={{ cursor: 'pointer', bgcolor: field.required ? '#fef3c7' : '#f1f5f9', color: field.required ? '#f59e0b' : '#94a3b8', fontWeight: 600, fontSize: '0.65rem' }} />
                    <IconButton size="small" onClick={() => removeField(sIdx, fIdx)} sx={{ color: '#ef4444' }}><RemoveIcon fontSize="small" /></IconButton>
                  </Box>
                ))}

                <Button size="small" startIcon={<AddIcon />} onClick={() => addField(sIdx)} sx={{ mt: 1, color: '#14B8A6' }}>
                  Adicionar Campo
                </Button>
              </CardContent>
            </Card>
          ))}

          <Button startIcon={<AddIcon />} onClick={addSection} sx={{ color: '#14B8A6' }}>Adicionar Seção</Button>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 }, flexShrink: 0, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
          <Button fullWidth={isMobile} onClick={() => setOpenDialog(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button fullWidth={isMobile} variant="contained" onClick={handleSave} sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none' }}>
            {editing ? 'Atualizar' : 'Criar Modelo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AnamnesisTemplates;
